import { useEffect } from "react";
import { SHORTCUT_DEFS } from "./shortcut-defs";
import {
	isMacPlatform,
	resolveShortcut,
	shouldBypassShortcutsForRunningTerminal,
	type ShortcutContext,
} from "./shortcuts";

function shortcutContext(event: KeyboardEvent): ShortcutContext {
	const target = event.target instanceof Element ? event.target : null;
	const contentEditableTarget = target?.closest<HTMLElement>("[contenteditable]");
	return {
		isComposing: event.isComposing,
		defaultPrevented: event.defaultPrevented,
		hasOpenDialog: document.querySelector("dialog[open]") !== null,
		inMonaco: Boolean(target?.closest(".monaco-editor")),
		inEditableField:
			Boolean(target?.closest("input, textarea, select")) ||
			contentEditableTarget?.isContentEditable === true,
		inRunningTerminal: Boolean(target?.closest("[data-terminal-running]")),
	};
}

/** アプリケーション共通のキーボードショートカットを window に登録する。 */
export function useGlobalShortcuts(): void {
	useEffect(() => {
		const isMac = isMacPlatform();
		const handleKeyDown = (event: KeyboardEvent) => {
			// run を持つショートカットは全て mod/ctrl 修飾を必須とするため、押されていなければ
			// 判定コストをかけない。run を持たない一覧表示専用の定義 (F12・⌥Z など) はここを
			// 素通りするが、実処理はエディタ側にあるので問題ない。
			if (!event.metaKey && !event.ctrlKey) return;
			const context = shortcutContext(event);
			// コマンド実行中のターミナルでは Ctrl 系をコマンドへ渡すため、アプリのショートカット処理を行わない。
			if (shouldBypassShortcutsForRunningTerminal(context, event)) return;
			const { shortcut, forcePreventDefault } = resolveShortcut(
				SHORTCUT_DEFS,
				event,
				context,
				isMac,
			);
			if (shortcut) {
				event.preventDefault();
				shortcut.run?.();
				return;
			}

			// 無効/スキップ状態でもブラウザの既定動作だけは止めたいショートカット
			// （quick-open のブラウザ印刷ダイアログ抑止など）を拾う。
			if (forcePreventDefault) event.preventDefault();
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);
}
